<template>
  <div class="admin-multi-image-upload">
    <div class="admin-multi-image-upload__list">
      <div
        v-for="(item, idx) in normalizedValue"
        :key="idx + '-' + item"
        class="admin-multi-image-upload__item"
      >
        <div class="admin-multi-image-upload__box" :style="boxStyle">
          <img :src="toFullUrl(item)" class="admin-multi-image-upload__pic" :style="boxStyle">
          <div class="admin-multi-image-upload__overlay">
            <div class="admin-multi-image-upload__actions">
              <i
                class="el-icon-view admin-multi-image-upload__action"
                title="预览"
                @click.stop="openPreview(idx)"
              />
              <i
                class="el-icon-delete admin-multi-image-upload__action"
                title="删除"
                @click.stop="removeAt(idx)"
              />
            </div>
          </div>
        </div>
      </div>

      <el-upload
        v-if="normalizedValue.length < limit"
        class="admin-multi-image-upload__uploader pic-uploader-component"
        action="#"
        :show-file-list="false"
        :http-request="handleUpload"
        :before-upload="beforeUpload"
        accept="image/*"
        :disabled="disabled || uploading"
      >
        <div class="admin-multi-image-upload__box admin-multi-image-upload__box--add" :style="boxStyle">
          <i v-if="!uploading" class="el-icon-plus admin-multi-image-upload__add-icon" :style="iconStyle" />
          <i v-else class="el-icon-loading admin-multi-image-upload__add-icon" :style="iconStyle" />
        </div>
      </el-upload>
    </div>

    <el-dialog
      title="图片预览"
      :visible.sync="previewDialogVisible"
      width="720px"
      append-to-body
      top="8vh"
    >
      <div class="admin-multi-image-upload__preview-wrap">
        <img v-if="previewDialogUrl" :src="previewDialogUrl" class="admin-multi-image-upload__preview-img">
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { uploadH5AdminImage } from '@/api/h5-admin-redbook'
import { resolveUploadUrl } from '@/utils/uploadBase'

export default {
  name: 'AdminMultiImageUpload',
  props: {
    value: {
      type: Array,
      default: () => []
    },
    limit: {
      type: Number,
      default: 5
    },
    disabled: {
      type: Boolean,
      default: false
    },
    width: {
      type: [Number, String],
      default: 84
    },
    height: {
      type: [Number, String],
      default: 84
    }
  },
  data() {
    return {
      uploading: false,
      previewDialogVisible: false,
      previewDialogUrl: ''
    }
  },
  computed: {
    normalizedValue() {
      if (!Array.isArray(this.value)) return []
      return this.value.filter(Boolean)
    },
    boxStyle() {
      const width = `${this.maxWidthPx}px`
      const height = `${this.maxHeightPx}px`
      return { width, height }
    },
    iconStyle() {
      const width = this.boxStyle.width
      const height = this.boxStyle.height
      return {
        width,
        height,
        lineHeight: height
      }
    },
    maxWidthPx() {
      return this.toPx(this.width, 84)
    },
    maxHeightPx() {
      return this.toPx(this.height, 84)
    }
  },
  methods: {
    toPx(val, fallback) {
      if (typeof val === 'number' && Number.isFinite(val)) return val
      const parsed = parseInt(val, 10)
      if (Number.isFinite(parsed) && parsed > 0) return parsed
      return fallback
    },
    toFullUrl(raw) {
      return resolveUploadUrl(raw)
    },
    openPreview(idx) {
      const url = this.toFullUrl(this.normalizedValue[idx])
      if (!url) return
      this.previewDialogUrl = url
      this.previewDialogVisible = true
    },
    beforeUpload(file) {
      const isImage = /^image\//i.test(file.type || '')
      if (!isImage) {
        this.$message.error('只能上传图片文件')
        return false
      }
      const isLt10M = file.size / 1024 / 1024 < 10
      if (!isLt10M) {
        this.$message.error('图片大小不能超过 10MB')
        return false
      }
      return true
    },
    async handleUpload(uploadReq) {
      const file = uploadReq && uploadReq.file
      if (!file) return
      this.uploading = true
      try {
        const res = await uploadH5AdminImage(file)
        const url = (res.data && res.data.url) || ''
        if (!url) {
          this.$message.error('上传失败：接口未返回图片地址')
          return
        }
        const next = [...this.normalizedValue, String(url).trim()]
        this.$emit('input', next)
        this.$emit('change', next)
        this.$message.success('上传成功')
      } catch (e) {
        // 错误提示由 adminRequest 拦截器处理
      } finally {
        this.uploading = false
      }
    },
    removeAt(idx) {
      const next = [...this.normalizedValue]
      next.splice(idx, 1)
      this.$emit('input', next)
      this.$emit('change', next)
    }
  }
}
</script>

<style scoped lang="less">
.admin-multi-image-upload__list {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 8px;
}

.admin-multi-image-upload__item {
  position: relative;
}

.admin-multi-image-upload__box {
  position: relative;
  box-sizing: border-box;
  overflow: hidden;
  background: #fff;
}

.admin-multi-image-upload__pic {
  display: block;
  object-fit: cover;
  background: #fff;
}

.admin-multi-image-upload__overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  opacity: 0;
  transition: opacity 0.2s;
  pointer-events: none;
}

.admin-multi-image-upload__item:hover .admin-multi-image-upload__overlay {
  opacity: 1;
}

.admin-multi-image-upload__actions {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  gap: 10px;
  pointer-events: auto;
}

.admin-multi-image-upload__action {
  color: rgba(255, 255, 255, 0.9);
  font-size: 18px;
  cursor: pointer;
}

::v-deep .pic-uploader-component .el-upload {
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  display: inline-block;
  vertical-align: top;
  background: #fff;
  box-sizing: border-box;
}

::v-deep .pic-uploader-component .el-upload:hover {
  border-color: #409eff;
}

.admin-multi-image-upload__item .admin-multi-image-upload__box {
  border: 1px solid #ebeef5;
  border-radius: 6px;
}

.admin-multi-image-upload__add-icon {
  font-size: 24px;
  color: #8c939d;
  text-align: center;
  display: block;
}

.admin-multi-image-upload__preview-wrap {
  width: 100%;
  max-height: 70vh;
  overflow: auto;
  text-align: center;
}

.admin-multi-image-upload__preview-img {
  max-width: 100%;
  max-height: 68vh;
}
</style>
